import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col } from '@/components/ui';
import FiltersCard from '../../../components/FiltersCard';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { vendorService } from '../../../services/vendorService';
import { areasService } from '../../../services/areasService';
import { PO_TYPE_OPTIONS, PoStatus, PoType } from '../../../services/purchaseOrderService';

export interface PurchaseOrderFilterState {
  vendorIds: number[];
  types: string[];
  serviceAreaIds: number[];
  foremanAreaIds: number[];
  statuses: string[];
}

export const EMPTY_PO_FILTERS: PurchaseOrderFilterState = {
  vendorIds: [],
  types: [],
  serviceAreaIds: [],
  foremanAreaIds: [],
  statuses: [],
};

interface Props {
  filters: PurchaseOrderFilterState;
  onFilterChange: (filters: PurchaseOrderFilterState) => void;
  onClearFilters: () => void;
}

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

/**
 * Searchable multi-select filters, matching the convention in AllTasksFilters and
 * TaskFilters. Vendor leads and is the widest: HE-P03 records Gord identifying vendor as the
 * primary lookup in practice, and it is also the longest list, so the in-dropdown search
 * earns the most there.
 */
const PurchaseOrdersFilters: React.FC<Props> = ({ filters, onFilterChange, onClearFilters }) => {
  const [vendors, setVendors] = useState<Array<{ id: number; name: string }>>([]);
  const [serviceAreas, setServiceAreas] = useState<Array<{ id: number; name: string }>>([]);
  const [foremanAreas, setForemanAreas] = useState<
    Array<{ id: number; name: string; serviceAreaId?: number }>
  >([]);

  useEffect(() => {
    vendorService.getAllVendors().then(setVendors).catch(() => setVendors([]));
    areasService
      .getServiceAreas()
      .then((areas: any[]) => setServiceAreas(areas ?? []))
      .catch(() => setServiceAreas([]));
    areasService
      .getForemanAreas()
      .then((areas: any[]) => setForemanAreas(areas ?? []))
      .catch(() => setForemanAreas([]));
  }, []);

  const set = <K extends keyof PurchaseOrderFilterState>(
    field: K,
    value: PurchaseOrderFilterState[K]
  ) => {
    const next = { ...filters, [field]: value };
    // Narrowing the service areas invalidates any foreman area chosen under one that is no
    // longer selected - leaving it would filter to nothing with no visible reason.
    if (field === 'serviceAreaIds') {
      const allowed = new Set(
        foremanAreas
          .filter(
            (fa) =>
              !(value as number[]).length ||
              (fa.serviceAreaId !== undefined && (value as number[]).includes(fa.serviceAreaId))
          )
          .map((fa) => fa.id)
      );
      next.foremanAreaIds = filters.foremanAreaIds.filter((id) => allowed.has(id));
    }
    onFilterChange(next);
  };

  const visibleForemanAreas = useMemo(
    () =>
      filters.serviceAreaIds.length
        ? foremanAreas.filter(
            (fa) => fa.serviceAreaId !== undefined && filters.serviceAreaIds.includes(fa.serviceAreaId)
          )
        : foremanAreas,
    [foremanAreas, filters.serviceAreaIds]
  );

  const hasActiveFilters =
    !!filters.vendorIds.length ||
    !!filters.types.length ||
    !!filters.serviceAreaIds.length ||
    !!filters.foremanAreaIds.length ||
    !!filters.statuses.length;

  return (
    <FiltersCard
      title="Purchase Order Filters"
      subtitle="Filter by vendor, type, area, and status"
      showClearButton
      onClear={onClearFilters}
      hasActiveFilters={hasActiveFilters}
    >
      <Row>
        <Col md={4}>
          <MultiSelectDropdown
            options={vendors}
            value={filters.vendorIds}
            onChange={(v) => set('vendorIds', v.map(Number))}
            getValue={(o) => o.id}
            formatOption={(o) => o.name}
            placeholder="All Vendors"
            label="Vendor"
            className="tw-w-full"
          />
        </Col>
        <Col md={2}>
          <MultiSelectDropdown
            options={PO_TYPE_OPTIONS}
            value={filters.types}
            onChange={(v) => set('types', v.map(String))}
            getValue={(o) => o.value}
            formatOption={(o) => o.label}
            placeholder="All Types"
            label="Type"
            className="tw-w-full"
            // The six types are a fixed, meaningful order from the source sheet.
            preserveOptionsOrder
          />
        </Col>
        <Col md={2}>
          <MultiSelectDropdown
            options={serviceAreas}
            value={filters.serviceAreaIds}
            onChange={(v) => set('serviceAreaIds', v.map(Number))}
            getValue={(o) => o.id}
            formatOption={(o) => o.name}
            placeholder="All Service Areas"
            label="Service Area"
            className="tw-w-full"
          />
        </Col>
        <Col md={2}>
          <MultiSelectDropdown
            options={visibleForemanAreas}
            value={filters.foremanAreaIds}
            onChange={(v) => set('foremanAreaIds', v.map(Number))}
            getValue={(o) => o.id}
            formatOption={(o) => o.name}
            placeholder="All Foreman Areas"
            label="Foreman Area"
            className="tw-w-full"
          />
        </Col>
        <Col md={2}>
          <MultiSelectDropdown
            options={STATUS_OPTIONS}
            value={filters.statuses}
            onChange={(v) => set('statuses', v.map(String))}
            getValue={(o) => o.value}
            formatOption={(o) => o.label}
            placeholder="All Statuses"
            label="Status"
            className="tw-w-full"
            // Two options; the search box would be noise.
            showSearch={false}
            preserveOptionsOrder
          />
        </Col>
      </Row>
    </FiltersCard>
  );
};

export type { PoStatus, PoType };
export default PurchaseOrdersFilters;
